import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthState } from '../../context/AuthContext';
import customFetch from '../../api/customFetch';

const Results = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isLoggedIn, user, loading } = useAuthState();
  const [results, setResults] = useState(null);
  const [loadingResults, setLoadingResults] = useState(true);

  useEffect(() => {
    if (loading) return;
    
    if (!isLoggedIn) {
      navigate('/user/login');
      return;
    }

    fetchResults();
  }, [slug, isLoggedIn, loading]);

  const fetchResults = async () => {
    try {
      const response = await customFetch.get(`/progress/${slug}/results`);
      setResults(response.data.results);
    } catch (error) {
      console.error('Error fetching results:', error);
      // אם אין תוצאות, נווט חזרה לדשבורד
      navigate('/user/dashboard');
    } finally {
      setLoadingResults(false);
    }
  };

  if (loading || loadingResults) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'var(--bg)',
        direction: 'rtl'
      }}>
        <div style={{ textAlign: 'center', color: 'var(--text)' }}>
          <div style={{ fontSize: '1.2rem', marginBottom: '10px' }}>טוען...</div>
          <div>טוען תוצאות קורס</div>
        </div>
      </div>
    );
  }

  if (!isLoggedIn || !results) {
    return null;
  }

  const passed = results.passed;
  const passColor = passed ? '#27ae60' : '#e74c3c';

  return (
    <div className="page" dir="rtl" style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      background: 'var(--bg)'
    }}>
      <div style={{
        background: 'var(--card)',
        padding: '40px',
        borderRadius: '24px',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
        textAlign: 'center',
        maxWidth: '500px',
        width: '100%',
        margin: '20px'
      }}>
        {/* אייקון התוצאה */}
        <div style={{
          fontSize: '4rem',
          marginBottom: '20px'
        }}>
          {passed ? '🎉' : '😔'}
        </div>

        {/* כותרת התוצאה */}
        <h2 style={{
          color: passColor,
          marginBottom: '20px',
          fontSize: '2rem',
          fontWeight: 'bold'
        }}>
          {passed ? 'מזל טוב! עברת את הבחינה' : 'לא עברת הפעם'}
        </h2>

        {/* פרטי התוצאות */}
        <div style={{
          background: passed ? '#d5f4e6' : '#fdf2f2',
          padding: '24px',
          borderRadius: '16px',
          marginBottom: '30px',
          border: `2px solid ${passColor}`
        }}>
          <div style={{
            fontSize: '3rem',
            fontWeight: 'bold',
            color: passColor,
            marginBottom: '10px'
          }}>
            {results.score}%
          </div>
          
          <div style={{
            color: '#333',
            fontSize: '1.1rem',
            marginBottom: '15px'
          }}>
            ענית נכון על {results.correctAnswers} מתוך {results.totalQuestions} שאלות
          </div>

          <div style={{
            color: '#666',
            fontSize: '0.9rem'
          }}>
            {passed 
              ? `עברת את הבחינה בהצלחה! (נדרש: ${results.passScore}%+)`
              : `לא עברת את הבחינה (נדרש: ${results.passScore}%+)`
            }
          </div>
        </div>

        {/* פרטי המשתמש */}
        <div style={{
          background: 'var(--card)',
          padding: '16px',
          borderRadius: '12px',
          marginBottom: '30px',
          fontSize: '0.9rem',
          color: 'var(--text)'
        }}>
          <div>לומד: {results.user?.fullName || user?.fullName || user?.username}</div>
          <div>תאריך: {new Date(results.completedAt).toLocaleDateString('he-IL')}</div>
          <div>קורס: {results.courseTitle}</div>
        </div>

        {/* כפתורי פעולה */}
        <div style={{
          display: 'flex',
          gap: '15px',
          justifyContent: 'center'
        }}>
          <button 
            onClick={() => navigate('/user/dashboard')}
            style={{
              background: 'var(--accent)',
              color: 'var(--lightText)',
              border: 'none',
              padding: '14px 28px',
              borderRadius: '16px',
              cursor: 'pointer',
              fontSize: '1.1rem',
              fontWeight: '700',
              flex: 1
            }}
          >
            חזור לדשבורד
          </button>

          <button 
            onClick={() => navigate(`/${slug}`)}
            style={{
              background: passed ? '#f39c12' : '#3498db',
              color: 'white',
              border: 'none',
              padding: '14px 28px',
              borderRadius: '16px',
              cursor: 'pointer',
              fontSize: '1.1rem',
              fontWeight: '700',
              flex: 1
            }}
          >
            {passed ? 'בצע שוב' : 'נסה שוב'}
          </button>
        </div>

        {/* הודעה נוספת */}
        <div style={{
          marginTop: '20px',
          fontSize: '0.85rem',
          color: 'var(--text)',
          lineHeight: '1.5'
        }}>
          {passed 
            ? 'התוצאה שלך נשמרה במערכת. תוכל לראות את ההישגים שלך בדשבורד.'
            : 'אל תתייאש! תוכל לנסות שוב בכל עת. חזור על החומר והמשך להתקדם.'
          }
        </div>
      </div>
    </div>
  );
};

export default Results;